import React, { useState } from 'react';
import { X, KeyRound, Loader2, CheckCircle2, User, Package, Trash2, AlertTriangle, Pencil, Save, Calendar, LayoutGrid, ShoppingBag, CalendarDays, MessageSquare, Shield, Mail, Smartphone } from 'lucide-react';
import SmsConversationTimeline from './SmsConversationTimeline';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import OrderListRow from './OrderListRow';
import OrderDetailModal from './OrderDetailModal';
import CustomerCrmPanel from './crm/CustomerCrmPanel';
import BookingEmailComposer from './BookingEmailComposer';
import BookingSmsComposer from './BookingSmsComposer';
import CaseSupportLink from './cases/CaseSupportLink';
import RecentCasesList from './cases/RecentCasesList';
import { EXPERIENCE_DATA, RIG_DATA } from '@/components/booking/bookingDefaults';

const inputCls = "w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-primary/60 transition-colors placeholder:text-white/20";

const TABS = [
  { id: 'overview', label: 'OVERVIEW', icon: LayoutGrid },
  { id: 'orders', label: 'ORDERS', icon: ShoppingBag },
  { id: 'bookings', label: 'BOOKINGS', icon: CalendarDays },
  { id: 'crm', label: 'CRM', icon: MessageSquare },
  { id: 'sms', label: 'SMS', icon: Smartphone },
  { id: 'admin', label: 'ADMIN', icon: Shield },
];

export default function UserDetailModal({ user, onClose, onSave }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const handleCrmNavigate = (path) => {
    onClose();
    navigate(path);
  };
  const [tab, setTab] = useState('overview');
  const [activeOrder, setActiveOrder] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [showSms, setShowSms] = useState(false);

  // Synthetic booking-like object so the email/SMS composers can be reused
  // verbatim from the Manage Bookings page without a new backend function.
  const contactBooking = {
    customer_name: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.full_name || 'Customer',
    customer_email: user.email || '',
    customer_phone: user.phone || '',
  };
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteError, setDeleteError] = useState('');

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    phone: user.phone || '',
    role: user.role || 'user',
    delivery_address_line1: user.delivery_address_line1 || '',
    delivery_address_line2: user.delivery_address_line2 || '',
    delivery_city: user.delivery_city || '',
    delivery_postcode: user.delivery_postcode || '',
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  const set = (key) => (e) => setEditForm(f => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError('');
    setSaveSuccess(false);
    const res = await base44.functions.invoke('adminUpdateUser', { userId: user.id, data: editForm });
    if (res.data?.error) {
      setSaveError(res.data.error);
      setSaveLoading(false);
      return;
    }
    setSaveLoading(false);
    setSaveSuccess(true);
    setEditing(false);
    if (onSave) onSave(editForm);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleDelete = async () => {
    if (deleteStep === 0) { setDeleteStep(1); return; }
    setDeleteStep(2);
    setDeleteError('');
    const res = await base44.entities.User.delete(user.id);
    if (res?.error) {
      setDeleteError(res.error);
      setDeleteStep(1);
    } else {
      onClose();
    }
  };

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['admin-orders', user.email],
    queryFn: () => base44.functions.invoke('adminGetUserOrders', { email: user.email }).then(r => r.data?.orders || []),
  });

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['admin-user-bookings', user.email],
    queryFn: () => base44.entities.Booking.filter({ customer_email: user.email }, '-date', 200),
  });

  // Profile (timeline + stats) fetched once here so the Overview tab can show
  // the stats cards and the CRM tab can show the timeline without a duplicate
  // call.
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['crm-profile', user.email],
    queryFn: () => base44.functions.invoke('adminGetCustomerProfile', { email: user.email }).then((r) => r.data),
    enabled: !!user.email,
  });

  const handlePasswordReset = async () => {
    setPwError('');
    setPwSuccess(false);
    setPwLoading(true);
    const res = await base44.functions.invoke('adminResetPassword', { email: user.email });
    setPwLoading(false);
    if (res.data?.success) {
      setPwSuccess(true);
    } else {
      setPwError(res.data?.error || 'Failed to send reset email.');
    }
  };

  const stats = profileData?.stats || {};
  const statCards = [
    ['TOTAL SPEND', stats.total_spend != null ? `£${Number(stats.total_spend).toFixed(2)}` : '—'],
    ['ORDERS', stats.order_count ?? 0],
    ['BOOKINGS', stats.booking_count ?? 0],
    ['FEEDBACK', stats.feedback_count ?? 0],
    ['AVG RATING', stats.avg_feedback_rating ? `${stats.avg_feedback_rating}/10` : '—'],
    ['OPEN TASKS', stats.open_tasks ?? 0],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111115] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        {/* Sticky header + tab nav */}
        <div className="sticky top-0 bg-[#111115] z-10 border-b border-white/5">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 bg-primary/10 flex items-center justify-center text-primary font-black text-sm shrink-0">
                {user.full_name?.charAt(0)?.toUpperCase() || <User className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm truncate">
                  {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.full_name || 'Unknown'}
                </p>
                <p className="text-white/30 text-xs truncate">{user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => { setShowEmail((s) => !s); setShowSms(false); }}
                className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-3 py-1.5 border transition-colors ${showEmail ? 'border-primary text-primary bg-primary/10' : 'border-white/10 text-white/50 hover:text-white hover:border-primary/50'}`}
              >
                <Mail className="w-3.5 h-3.5" /> EMAIL
              </button>
              <button
                onClick={() => { setShowSms((s) => !s); setShowEmail(false); }}
                disabled={!contactBooking.customer_phone}
                title={contactBooking.customer_phone ? '' : 'No phone number on file'}
                className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${showSms ? 'border-primary text-primary bg-primary/10' : 'border-white/10 text-white/50 hover:text-white hover:border-primary/50'}`}
              >
                <MessageSquare className="w-3.5 h-3.5" /> TEXT
              </button>
              <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {(showEmail || showSms) && (
            <div className="px-6 pb-4">
              {showEmail && <BookingEmailComposer booking={contactBooking} onClose={() => setShowEmail(false)} />}
              {showSms && <BookingSmsComposer booking={contactBooking} onClose={() => setShowSms(false)} />}
            </div>
          )}

          <div className="flex overflow-x-auto">
            {TABS.map((t) => {
              const TabIcon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-[10px] font-bold tracking-wider whitespace-nowrap transition-colors -mb-px border-b-2 ${active ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white'}`}
                >
                  <TabIcon className="w-3 h-3" /> {t.label}
                  {t.id === 'orders' && !ordersLoading && ` (${ordersData?.length ?? 0})`}
                  {t.id === 'bookings' && !bookingsLoading && ` (${bookings.length})`}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* ── OVERVIEW ─────────────────────────────────────────────── */}
          {tab === 'overview' && (
            <div className="space-y-8">
              {/* Account Details */}
              <div>
                <div className="flex items-center justify-between mb-5 pb-3 border-b border-white/5">
                  <p className="text-primary text-[10px] font-bold tracking-widest">ACCOUNT DETAILS</p>
                  {!editing ? (
                    <button onClick={() => setEditing(true)}
                      className="flex items-center gap-1.5 text-white/40 hover:text-white text-[10px] font-bold tracking-wider transition-colors">
                      <Pencil className="w-3 h-3" /> EDIT
                    </button>
                  ) : (
                    <button onClick={() => { setEditing(false); setSaveError(''); }}
                      className="text-white/30 hover:text-white text-[10px] font-bold tracking-wider transition-colors">
                      CANCEL
                    </button>
                  )}
                </div>

                {saveSuccess && (
                  <div className="flex items-center gap-2 text-green-400 text-xs bg-green-400/10 border border-green-400/20 px-3 py-2 mb-4">
                    <CheckCircle2 className="w-4 h-4" /> Account updated successfully.
                  </div>
                )}
                {saveError && (
                  <p className="text-red-400 text-xs bg-red-400/10 border border-red-400/20 px-3 py-2 mb-4">{saveError}</p>
                )}

                {editing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">FIRST NAME</p>
                        <input className={inputCls} value={editForm.first_name} onChange={set('first_name')} placeholder="First name" />
                      </div>
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">LAST NAME</p>
                        <input className={inputCls} value={editForm.last_name} onChange={set('last_name')} placeholder="Last name" />
                      </div>
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">EMAIL (READ-ONLY)</p>
                        <input className={`${inputCls} opacity-40 cursor-not-allowed`} value={user.email} readOnly />
                      </div>
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">PHONE</p>
                        <input className={inputCls} value={editForm.phone} onChange={set('phone')} placeholder="Phone number" />
                      </div>
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">ROLE</p>
                        <select className={inputCls} value={editForm.role} onChange={set('role')}>
                          <option value="user" className="bg-[#0d0d0f]">User</option>
                          <option value="admin" className="bg-[#0d0d0f]">Admin</option>
                        </select>
                      </div>
                    </div>

                    <p className="text-primary text-[10px] font-bold tracking-widest pt-2 pb-2 border-b border-white/5">DELIVERY ADDRESS</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">ADDRESS LINE 1</p>
                        <input className={inputCls} value={editForm.delivery_address_line1} onChange={set('delivery_address_line1')} placeholder="Street address" />
                      </div>
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">ADDRESS LINE 2</p>
                        <input className={inputCls} value={editForm.delivery_address_line2} onChange={set('delivery_address_line2')} placeholder="Apt, suite, etc." />
                      </div>
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">CITY</p>
                        <input className={inputCls} value={editForm.delivery_city} onChange={set('delivery_city')} placeholder="City" />
                      </div>
                      <div>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">POSTCODE</p>
                        <input className={inputCls} value={editForm.delivery_postcode} onChange={set('delivery_postcode')} placeholder="Postcode" />
                      </div>
                    </div>

                    <button onClick={handleSave} disabled={saveLoading}
                      className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-3 transition-colors disabled:opacity-50 mt-2">
                      {saveLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {saveLoading ? 'SAVING...' : 'SAVE CHANGES'}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                    {[
                      ['FIRST NAME', editForm.first_name || user.first_name],
                      ['LAST NAME', editForm.last_name || user.last_name],
                      ['EMAIL', user.email],
                      ['PHONE', editForm.phone || user.phone],
                      ['ROLE', (editForm.role || user.role)?.toUpperCase()],
                      ['REGISTERED', user.created_date ? format(new Date(user.created_date), 'dd MMM yyyy') : null],
                      ['ADDRESS', [editForm.delivery_address_line1, editForm.delivery_city, editForm.delivery_postcode].filter(Boolean).join(', ')],
                    ].map(([label, value]) => value ? (
                      <div key={label}>
                        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">{label}</p>
                        <p className="text-white/80 text-sm">{value || '—'}</p>
                      </div>
                    ) : null)}
                  </div>
                )}
              </div>

              {/* Snapshot stats */}
              <div>
                <p className="text-primary text-[10px] font-bold tracking-widest mb-4 pb-3 border-b border-white/5">SNAPSHOT</p>
                {profileLoading ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {statCards.map(([l, v]) => (
                      <div key={l} className="border border-white/5 bg-[#0d0d0f] px-4 py-3">
                        <p className="text-white/30 text-[9px] font-bold tracking-widest">{l}</p>
                        <p className="text-white font-black text-lg mt-0.5">{v}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Support — the case this customer is already in, or a way in */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-white/5">
                  <p className="text-primary text-[10px] font-bold tracking-widest">SUPPORT</p>
                  <CaseSupportLink
                    customerEmail={user.email}
                    customerName={[user.first_name, user.last_name].filter(Boolean).join(' ') || user.full_name}
                    customerPhone={user.phone}
                  />
                </div>
                <RecentCasesList customerEmail={user.email} />
              </div>
            </div>
          )}

          {/* ── ORDERS ───────────────────────────────────────────────── */}
          {tab === 'orders' && (
            <div>
              {ordersLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : !ordersData || ordersData.length === 0 ? (
                <div className="flex items-center gap-3 text-white/30 text-xs py-6 border border-white/5 bg-[#0d0d0f] px-5">
                  <Package className="w-5 h-5 text-white/10 shrink-0" />
                  No orders found for this customer.
                </div>
              ) : (
                <div className="space-y-2">
                  {ordersData.map((order) => (
                    <OrderListRow key={order.id} order={order} onOpen={() => setActiveOrder(order)} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── BOOKINGS ────────────────────────────────────────────── */}
          {tab === 'bookings' && (
            <div>
              {bookingsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : bookings.length === 0 ? (
                <div className="flex items-center gap-3 text-white/30 text-xs py-6 border border-white/5 bg-[#0d0d0f] px-5">
                  <Calendar className="w-5 h-5 text-white/10 shrink-0" />
                  No bookings found for this customer.
                </div>
              ) : (
                <div className="space-y-2">
                  {bookings.map((b) => {
                    const exp = EXPERIENCE_DATA[b.experience_type];
                    const rig = RIG_DATA[b.rig_type];
                    return (
                      <div key={b.id} className="grid grid-cols-12 gap-3 items-center px-4 py-3 border border-white/5 bg-[#0d0d0f]">
                        <div className="col-span-12 sm:col-span-5 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{exp?.name || b.experience_type || 'Session'}</p>
                          <p className="text-white/30 text-xs truncate">{rig?.name || b.rig_type || '—'}</p>
                        </div>
                        <p className="col-span-7 sm:col-span-3 text-white/50 text-xs">
                          {b.date ? format(new Date(b.date), 'dd MMM yyyy') : '—'} · {b.time_slot}
                        </p>
                        <div className="col-span-3 sm:col-span-2">
                          <span className={`inline-flex text-[10px] font-bold tracking-wider px-2 py-0.5 ${
                            b.status === 'confirmed' ? 'bg-green-500/10 text-green-400'
                            : b.status === 'completed' ? 'bg-primary/10 text-primary'
                            : b.status === 'cancelled' ? 'bg-red-500/10 text-red-400'
                            : 'bg-white/5 text-white/40'
                          }`}>
                            {b.status?.toUpperCase()}
                          </span>
                        </div>
                        <p className="col-span-2 sm:col-span-2 text-right text-white/70 text-sm font-bold">
                          {b.is_loyalty_free ? <span className="text-primary">FREE</span> : `£${(b.total_price || 0).toFixed(2)}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── CRM ─────────────────────────────────────────────────── */}
          {tab === 'crm' && (
            <CustomerCrmPanel
              user={user}
              onUserSaved={onSave}
              profile={profileData}
              profileLoading={profileLoading}
              onNavigate={handleCrmNavigate}
              orders={ordersData || []}
              onEmailSent={() => queryClient.invalidateQueries({ queryKey: ['crm-profile', user.email] })}
            />
          )}

          {/* ── SMS THREAD ──────────────────────────────────────────── */}
          {tab === 'sms' && (
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest mb-4 pb-3 border-b border-white/5">SMS CONVERSATION</p>
              <SmsConversationTimeline
                phone={user.phone}
                customerName={[user.first_name, user.last_name].filter(Boolean).join(' ') || user.full_name}
                compact
              />
            </div>
          )}

          {/* ── ADMIN ───────────────────────────────────────────────── */}
          {tab === 'admin' && (
            <div className="space-y-8">
              {/* Password Reset */}
              <div>
                <p className="text-primary text-[10px] font-bold tracking-widest mb-4 pb-3 border-b border-white/5">RESET PASSWORD</p>
                <p className="text-white/30 text-xs mb-4">Send a password reset email to <span className="text-white">{user.email}</span>. The customer will receive a link to choose a new password.</p>

                {pwError && (
                  <p className="text-red-400 text-xs bg-red-400/10 border border-red-400/20 px-3 py-2 mb-3">{pwError}</p>
                )}
                {pwSuccess && (
                  <div className="flex items-center gap-2 text-green-400 text-xs bg-green-400/10 border border-green-400/20 px-3 py-2 mb-3">
                    <CheckCircle2 className="w-4 h-4" /> Reset email sent to {user.email}
                  </div>
                )}

                <button
                  onClick={handlePasswordReset}
                  disabled={pwLoading}
                  className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-3 transition-colors disabled:opacity-50"
                >
                  {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  {pwLoading ? 'SENDING...' : 'SEND PASSWORD RESET EMAIL'}
                </button>
              </div>

              {/* Delete Account */}
              <div>
                <p className="text-red-500/70 text-[10px] font-bold tracking-widest mb-4 pb-3 border-b border-red-500/10">DANGER ZONE</p>

                {deleteStep === 1 && (
                  <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 px-4 py-3 mb-4">
                    <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                    <p className="text-red-300 text-xs leading-relaxed">
                      <span className="font-bold">Are you absolutely sure?</span> This will permanently delete <span className="text-white">{user.full_name || user.email}</span>'s account. This action cannot be undone.
                    </p>
                  </div>
                )}

                {deleteError && (
                  <p className="text-red-400 text-xs bg-red-400/10 border border-red-400/20 px-3 py-2 mb-3">{deleteError}</p>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDelete}
                    disabled={deleteStep === 2}
                    className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold text-xs tracking-wider px-5 py-3 transition-colors disabled:opacity-50"
                  >
                    {deleteStep === 2 ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    {deleteStep === 0 ? 'DELETE ACCOUNT' : deleteStep === 2 ? 'DELETING...' : 'CONFIRM DELETE'}
                  </button>
                  {deleteStep === 1 && (
                    <button
                      onClick={() => setDeleteStep(0)}
                      className="text-white/30 hover:text-white text-xs font-bold tracking-wider px-4 py-3 border border-white/10 hover:border-white/25 transition-all"
                    >
                      CANCEL
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {activeOrder && (
        <OrderDetailModal
          order={activeOrder}
          userEmail={user.email}
          onClose={() => setActiveOrder(null)}
        />
      )}
    </div>
  );
}